/**
 * Реестр шрифтов для шаринга.
 *
 * `font_key` (greeting/caption.font_key) — динамический справочник из БД.
 * Бэк хранит только `key`; рендерит шрифт фронт. Неизвестный `key` → дефолт.
 */
const DEFAULT_STACK = '"Onest", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
/**
 * Курируемый набор под активные ключи из /share-presets (+ запасные).
 * Новый `key` в дашборде → дописать сюда запись.
 */
const FONT_REGISTRY = {
    onest: { stack: DEFAULT_STACK, family: "Onest" },
    manrope: {
        stack: '"Manrope", -apple-system, BlinkMacSystemFont, Arial, sans-serif',
        google: "Manrope:wght@400;600;700",
        family: "Manrope",
    },
    caveat: {
        stack: '"Caveat", cursive',
        google: "Caveat:wght@400;700",
        family: "Caveat",
    },
    lobster: { stack: '"Lobster", cursive', google: "Lobster", family: "Lobster" },
    "russo-one": {
        stack: '"Russo One", Arial, sans-serif',
        google: "Russo+One",
        family: "Russo One",
    },
    pacifico: { stack: '"Pacifico", cursive', google: "Pacifico", family: "Pacifico" },
    "yeseva-one": {
        stack: '"Yeseva One", Georgia, serif',
        google: "Yeseva+One",
        family: "Yeseva One",
    },
    "marck-script": {
        stack: '"Marck Script", cursive',
        google: "Marck+Script",
        family: "Marck Script",
    },
    podkova: {
        stack: '"Podkova", Georgia, serif',
        google: "Podkova:wght@400;700",
        family: "Podkova",
    },
    "rubik-mono-one": {
        stack: '"Rubik Mono One", Arial, sans-serif',
        google: "Rubik+Mono+One",
        family: "Rubik Mono One",
    },
    "playfair-display": {
        stack: '"Playfair Display", Georgia, serif',
        google: "Playfair+Display:wght@400;700",
        family: "Playfair Display",
    },
    // запасные
    roboto: {
        stack: '"Roboto", Arial, sans-serif',
        google: "Roboto:wght@400;700",
        family: "Roboto",
    },
    montserrat: {
        stack: '"Montserrat", Arial, sans-serif',
        google: "Montserrat:wght@400;600;800",
        family: "Montserrat",
    },
    "great-vibes": {
        stack: '"Great Vibes", cursive',
        google: "Great+Vibes",
        family: "Great Vibes",
    },
    comfortaa: {
        stack: '"Comfortaa", cursive',
        google: "Comfortaa:wght@400;700",
        family: "Comfortaa",
    },
    "dancing-script": {
        stack: '"Dancing Script", cursive',
        google: "Dancing+Script:wght@400;700",
        family: "Dancing Script",
    },
    merriweather: {
        stack: '"Merriweather", Georgia, serif',
        google: "Merriweather:wght@400;700",
        family: "Merriweather",
    },
};
const injectedFamilies = new Set();
const pendingLoads = new Map();
const FONT_WAIT_MS = 2500;
function injectStylesheet(google) {
    if (typeof document === "undefined" || injectedFamilies.has(google))
        return;
    injectedFamilies.add(google);
    try {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        // block — не показываем fallback, пока шрифт не готов (мы и так ждём fonts.load).
        link.href = `https://fonts.googleapis.com/css2?family=${google}&display=block`;
        link.crossOrigin = "anonymous";
        document.head.appendChild(link);
    }
    catch {
        /* сеть/блокировка — останемся на фолбэке */
    }
}
function withTimeout(promise, ms) {
    return Promise.race([
        promise,
        new Promise((resolve) => {
            window.setTimeout(resolve, ms);
        }),
    ]);
}
/**
 * Идемпотентно подгружает веб-шрифт для `key` (через <link> на Google Fonts).
 * Не ждёт готовности — для фоновой подгрузки (редактор).
 */
export function ensureFontLoaded(key) {
    void preloadShareFont(key);
}
/**
 * Подгружает шрифт и ждёт, пока он станет доступен для рендера
 * (через document.fonts.load). Так на публичной ссылке нет FOUT.
 */
export function preloadShareFont(key) {
    if (!key || typeof document === "undefined")
        return Promise.resolve();
    const normalized = key.toLowerCase();
    const entry = FONT_REGISTRY[normalized];
    if (!entry)
        return Promise.resolve();
    if (entry.google)
        injectStylesheet(entry.google);
    const family = entry.family;
    if (!family || !document.fonts?.load)
        return Promise.resolve();
    const existing = pendingLoads.get(normalized);
    if (existing)
        return existing;
    const load = withTimeout(document.fonts.load(`700 48px "${family}"`).then(() => undefined), FONT_WAIT_MS).then(() => undefined);
    pendingLoads.set(normalized, load);
    return load;
}
/** Параллельно подгрузить несколько ключей (каталог шрифтов в редакторе). */
export function preloadShareFonts(keys) {
    return Promise.all(keys.map((k) => preloadShareFont(k))).then(() => undefined);
}
/** Достаёт font_key из config шары (greeting или caption). */
export function extractFontKeyFromConfig(config) {
    if ("greeting" in config && config.greeting?.font_key) {
        return config.greeting.font_key;
    }
    if ("caption" in config && config.caption?.font_key) {
        return config.caption.font_key;
    }
    return undefined;
}
/** CSS font-family для `key` (или дефолт, если ключ неизвестен). */
export function resolveFontFamily(key) {
    if (!key)
        return DEFAULT_STACK;
    return FONT_REGISTRY[key.toLowerCase()]?.stack ?? DEFAULT_STACK;
}
export function isKnownFontKey(key) {
    return Boolean(key && FONT_REGISTRY[key.toLowerCase()]);
}
